import { type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updatePostRequest } from "../../../../redux/broadcast/broadcast.actions";
import { setEditingPost } from "../../../../redux/post-editing/actions";
import { selectorEditingPostId } from "../../../../redux/post-editing/selectors";
import Button from "../../../button/button";
import { Text } from "../../posts.styles";
import { usePostBroadcastContext } from "../../post-broadcast.context";
import * as CSS from "./post-text.styles";

type PostTextProps = {
	postId: string;
	text: string;
	quote?: boolean;
};

const PostText: FC<PostTextProps> = ({ postId, text, quote = false }) => {
	const dispatch = useDispatch();
	const editingPostId = useSelector(selectorEditingPostId);
	const { broadcastId } = usePostBroadcastContext();
	const isEditing = editingPostId === postId;
	const [draft, setDraft] = useState(text);

	useEffect(() => {
		if (isEditing) setDraft(text);
	}, [isEditing, text]);

	if (!isEditing) {
		return <Text>{quote ? `"${text}"` : text}</Text>;
	}

	const handleSave = () => {
		const trimmed = draft?.trim();
		if (!trimmed || !broadcastId) return;
		dispatch(updatePostRequest({ broadcastId, postId, text: trimmed }));
		dispatch(setEditingPost(null));
	};

	const handleCancel = () => {
		dispatch(setEditingPost(null));
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (event.key === "Escape") {
			handleCancel();
		}

		if (event.key === "Enter") {
			event.preventDefault();
			handleSave();
		}
	};

	return (
		<CSS.EditContainer>
			<CSS.Textarea
				value={draft}
				onChange={(e) => setDraft(e.target.value)}
				onKeyDown={handleKeyDown}
				autoFocus
			/>
			<CSS.ButtonRow>
				<Button type="button" onClick={handleSave} disabled={!draft?.trim()}>
					Save
				</Button>
				<Button type="button" onClick={handleCancel}>
					Cancel
				</Button>
			</CSS.ButtonRow>
		</CSS.EditContainer>
	);
};

export default PostText;
